import { forwardRef } from 'react';
import { Box, BoxProps } from '@mui/material';

const combineSx = (base: BoxProps['sx'], sx: BoxProps['sx']) =>
  sx ? [base, ...(Array.isArray(sx) ? sx : [sx])] : [base];

export const AdminDialogSurface = forwardRef<HTMLDivElement, BoxProps>(
  ({ sx, ...props }, ref) => (
    <Box
      {...props}
      ref={ref}
      role="dialog"
      aria-modal="true"
      data-admin-dialog-surface
      sx={combineSx(
        {
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: {
            xs: 'calc(100vw - 32px)',
            sm: 'min(960px, calc(100vw - 64px))'
          },
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100dvh - 32px)',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 24,
          outline: 'none'
        },
        sx
      )}
    />
  )
);

AdminDialogSurface.displayName = 'AdminDialogSurface';

export const AdminDialogHeader = ({ sx, ...props }: BoxProps) => (
  <Box
    component="header"
    {...props}
    data-admin-dialog-header
    sx={combineSx(
      {
        flex: '0 0 auto',
        px: { xs: 2, sm: 3 },
        py: 2,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        zIndex: 1
      },
      sx
    )}
  />
);

export const AdminDialogBody = ({ sx, ...props }: BoxProps) => (
  <Box
    {...props}
    data-admin-dialog-body
    sx={combineSx(
      {
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        px: { xs: 2, sm: 3 },
        py: 2
      },
      sx
    )}
  />
);

export const AdminDialogFooter = ({ sx, ...props }: BoxProps) => (
  <Box
    component="footer"
    {...props}
    data-admin-dialog-footer
    sx={combineSx(
      {
        flex: '0 0 auto',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        px: { xs: 2, sm: 3 },
        py: 2,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        zIndex: 1
      },
      sx
    )}
  />
);
